import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { CachePaginate, ExtendedOtherProductStore, IOtherProduct } from '../types/types';
import { createProduct, deleteProduct, getProductsByServiceId, getProducts, updateProduct } from '../utils/api';

const useOtherProduct: UseBoundStore<StoreApi<ExtendedOtherProductStore>> = create((set, get) => ({
  products: [],
  productsBySrv: [],
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  page: 1,
  pageBySrv: 1,
  hasMore: true,
  hasMoreBySrv: true,
  meta: null,
  metaBySrv: null,
  currentServiceId: null,
  isLoadingNextPage: false,
  cache: {},
  hasFetched: false,

  fetchProducts: async (page = null, reset = false) => {
    try {
      set({ ...(reset ? { fetchLoading: true } : { isLoadingNextPage: true }) });
      const { success, message, data, meta } = await getProducts(page, page ? 15 : null);
      if (!success) throw new Error(message);

      set((state) => {
        const newData = page
          ? data.filter((item: IOtherProduct) => !state.products.some((p) => p.product_id === item.product_id))
          : data;

        // console.log(`Fetching page ${page}, reset: ${reset}, new items: ${newData.length}`);

        let updatedProducts;
        if (reset) {
          updatedProducts = data;
        } else if (page) {
          updatedProducts = [...state.products, ...newData];
        } else {
          updatedProducts = data;
        }

        const isPaginated = meta?.paginated ?? false;
        const totalPages = meta?.totalPages ?? 1;
        const currentPage = page || 1;
        const hasMore = isPaginated ? currentPage < totalPages : false;

        return {
          products: updatedProducts,
          page: currentPage,
          hasMore: data.length > 0 && hasMore,
          meta: meta ?? null,
          hasFetched: true,
        };
      });
      return { message, success, data };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false, isLoadingNextPage: false });
    }
  },

  fetchProductsByServiceId: async (pageBySrv = null, reset = false, serviceId = null) => {
    const currentState = get();
    const cacheKey = `${serviceId}-${pageBySrv || 1}`;

    if (!reset && currentState.cache[cacheKey]) {
      set({
        productsBySrv: currentState.cache[cacheKey].data,
        pageBySrv: currentState.cache[cacheKey].page,
        hasMoreBySrv: currentState.cache[cacheKey].hasMore,
        meta: currentState.cache[cacheKey].meta,
        currentServiceId: serviceId,
        fetchLoading: false,
        isLoadingNextPage: false,
      });
      return { success: true, message: 'Loaded from cache', data: currentState.cache[cacheKey].data };
    }

    try {
      set({ ...(reset ? { fetchLoading: true } : { isLoadingNextPage: true }) });
      const { success, message, data, meta } = await getProductsByServiceId(
        pageBySrv,
        pageBySrv ? 10 : null,
        serviceId,
      );
      if (!success) throw new Error(message);

      set((prevState) => {
        const newData = pageBySrv
          ? data.filter((item: IOtherProduct) => !prevState.productsBySrv.some((p) => p.product_id === item.product_id))
          : data;

        let updatedProductsBySrv: IOtherProduct[];
        if (reset) {
          updatedProductsBySrv = data;
        } else if (pageBySrv) {
          updatedProductsBySrv = [...prevState.productsBySrv, ...newData];
        } else {
          updatedProductsBySrv = data;
        }
        const updatedCache: CachePaginate<IOtherProduct> = {
          ...prevState.cache,
          [cacheKey]: {
            data: updatedProductsBySrv,
            page: pageBySrv || 1,
            hasMore: meta.paginated ? (pageBySrv || 1) < (meta.totalPages || 1) : false,
            meta,
          },
        };

        return {
          productsBySrv: updatedProductsBySrv,
          pageBySrv: pageBySrv || 1,
          hasMoreBySrv: meta.paginated ? (pageBySrv || 1) < (meta.totalPages || 1) : false,
          meta,
          currentServiceId: serviceId,
          cache: updatedCache,
          hasFetched: true,
        };
      });
      return { message, success, data };
    } catch (error) {
      const message = getErrorMessage(error);
      return { success: false, message };
    } finally {
      set({ fetchLoading: false, isLoadingNextPage: false });
    }
  },

  addProduct: async (productData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createProduct(productData);
      if (!success) throw new Error(message);
      set((state) => ({ products: state.products ? [...state.products, data] : [data] }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editProduct: async (id, productData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updateProduct(id, productData);
      if (!success) throw new Error(message);
      set((state) => ({
        products: state.products?.map((product) => (product.product_id === id ? data : product)),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removeProduct: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deleteProduct(id);
      if (!success) throw new Error(message);
      set((state) => ({
        products: state.products?.filter((product) => product.product_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default useOtherProduct;
